import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useQuery } from "react-query";
import { yupResolver } from "@hookform/resolvers/yup";
import { object, string } from "yup";

import { cuaHangApi } from "src/1/apis/cua_hang.api";
import { khoApi } from "src/1/apis/kho.api";
import {
  CButton,
  CInput,
  CSelect,
  CTextarea,
} from "src/common/components/controls";
import { CDialog } from "src/common/components/others";

const defaultValues = {
  code: "",
  name: "",
  store_code: "",
  address: "",
};

const resolver = yupResolver(
  object({
    name: string().trim().required("Tên kho không được để trống"),
    store_code: string().trim().required("Mã cửa hàng không được để trống"),
    address: string().trim().required("Địa chỉ kho không được để trống"),
  })
);

export const WarehouseFormModal = forwardRef(({ refetch }, ref) => {
  //#region Data
  const [open, setOpen] = useState(false);
  const [id, setId] = useState("");

  const { data } = useQuery({
    queryKey: ["thong-tin-chi-tiet-kho", id],
    queryFn: () => khoApi.getById(id),
    enabled: open && Boolean(id),
    select: (response) => response?.data?.data,
  });

  const { data: stores = [] } = useQuery({
    queryKey: ["danh-sach-cua-hang"],
    queryFn: () => cuaHangApi.getMyStore(),
    enabled: open,
    select: (response) =>
      response?.data?.data?.map((e) => ({ label: e?.name, value: e.code })),
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm({ defaultValues, resolver });
  //#endregion

  useEffect(() => {
    if (data) {
      reset({
        code: data.code,
        name: data.name,
        store_code: data.store_code,
        address: data.address,
      });
    }
  }, [data]);

  //#region Event
  const onClose = () => {
    reset(defaultValues);
    setId("");
    setOpen(false);
  };

  const onSubmit = handleSubmit(async (values) => {
    try {
      if (id) {
        await khoApi.update(id, values);
        noti("success", "Cập nhật kho thành công");
      } else {
        await khoApi.create(values);
        noti("success", "Tạo mới kho thành công");
      }
      refetch?.();
      onClose();
    } catch (error) {
      noti("error", error?.message ?? "Lưu kho thất bại");
    }
  });
  //#endregion

  useImperativeHandle(ref, () => ({
    open: (_id) => {
      if (_id) {
        setId(_id);
      }
      setOpen(true);
    },
  }));

  //#region Render
  return (
    <CDialog
      show={open}
      onClose={onClose}
      title={`${id ? "Sửa" : "Thêm"} kho hàng hóa`}
      footer={
        <div className="flex flex-row">
          <CButton color="danger" onClick={onClose} disabled={isSubmitting}>
            Hủy
          </CButton>
          <CButton onClick={onSubmit} disabled={isSubmitting}>
            Lưu
          </CButton>
        </div>
      }
    >
      <div className="grid grid-cols-2 gap-2">
        <Controller
          control={control}
          name="code"
          render={({ field }) => (
            <CInput
              {...field}
              label="Mã kho"
              placeholder="Mã hệ thống tự tạo"
              disabled
            />
          )}
        />
        <Controller
          control={control}
          name="store_code"
          render={({ field }) => (
            <CSelect
              {...field}
              options={stores}
              select="value"
              label="Chi nhánh"
              placeholder="Chọn chi nhánh"
            />
          )}
        />
        <Controller
          control={control}
          name="name"
          render={({ field }) => (
            <CInput {...field} label="Tên kho" placeholder="Nhập tên kho" />
          )}
        />
        <Controller
          control={control}
          name="address"
          render={({ field }) => (
            <CTextarea
              {...field}
              label="Địa chỉ kho"
              placeholder="Nhập địa chỉ kho"
            />
          )}
        />
      </div>
    </CDialog>
  );
  //#endregion
});
